import { DeleteOutlined, HolderOutlined, PlusOutlined } from '@ant-design/icons';
import * as productApi from '@apis/product';
import * as feedsV2Api from '@apis/feeds-v2';
import { SelectProductModal } from '@components/product/SelectProductModal';
import ProductEditView from '@components/product/ProductEditView';
import { useAuthentication } from '@hooks/authentication';
import { ProductModel } from '@models/product';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cdnImageUrl } from '@utils/image';
import {
  Button,
  Card,
  Checkbox,
  Divider,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const { Title, Text } = Typography;

// ─── 타입 정의 ──────────────────────────────────────────────────────────────

interface SubCarousel {
  id: string;
  title: string;
  active: boolean;
  autoplay: boolean;
  delay: number;
  selectedProductIds: string[];
}

const TimeSalesTypeTag: React.FC<{ type?: string }> = ({ type }) => {
  if (type === '오픈특가') return <Tag color="purple">오픈특가</Tag>;
  return <Tag color="blue">얼리버드</Tag>;
};

// ─── 드래그 가능한 테이블 Row (전체 상품 목록용) ─────────────────────────────

interface SortableRowProps {
  id: string;
  children?: React.ReactNode;
  [key: string]: any;
}

const SortableRow: React.FC<SortableRowProps> = ({
  id,
  children,
  style: propStyle,
  onMouseEnter: propOnMouseEnter,
  onMouseLeave: propOnMouseLeave,
  ...props
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const [isHovered, setIsHovered] = useState(false);

  // propStyle을 먼저 펼친 뒤 우리 스타일로 덮어써서 cursor가 항상 적용되도록 함
  const style: React.CSSProperties = {
    ...propStyle,
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: isDragging ? 'grabbing' : 'grab',
    backgroundColor: isDragging ? '#e6f4ff' : isHovered ? '#f5f9ff' : undefined,
    boxShadow: isDragging ? '0 2px 8px rgba(0,0,0,0.12)' : undefined,
  };

  return (
    <tr
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      {...props}
      style={style}
      onMouseEnter={(e) => {
        setIsHovered(true);
        propOnMouseEnter?.(e);
      }}
      onMouseLeave={(e) => {
        setIsHovered(false);
        propOnMouseLeave?.(e);
      }}>
      {children}
    </tr>
  );
};

// ─── 드래그 가능한 기획전 미리보기 카드 (가로 스크롤용) ──────────────────────

interface SortableCarouselCardProps {
  sc: SubCarousel;
  productCount: number;
  onEdit: () => void;
  onRemove: () => void;
  onUpdate: (patch: Partial<Pick<SubCarousel, 'active' | 'autoplay' | 'delay'>>) => void;
}

const SortableCarouselCard: React.FC<SortableCarouselCardProps> = ({
  sc,
  productCount,
  onEdit,
  onRemove,
  onUpdate,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sc.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    flexShrink: 0,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card
        size="small"
        style={{
          width: 200,
          border: sc.active ? '1px solid #d9e8ff' : '1px solid #e0e0e0',
          background: sc.active ? '#f0f5ff' : '#f5f5f5',
          cursor: isDragging ? 'grabbing' : 'default',
        }}
        bodyStyle={{ padding: '10px 12px' }}>
        {/* 드래그 핸들 */}
        <div
          {...attributes}
          {...listeners}
          style={{
            cursor: isDragging ? 'grabbing' : 'grab',
            color: '#ccc',
            fontSize: 14,
            marginBottom: 6,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
          <HolderOutlined />
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Button
              size="small"
              type="text"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}>
              편집
            </Button>
            <Popconfirm
              title="이 기획전을 삭제하시겠습니까?"
              onConfirm={(e) => {
                e?.stopPropagation();
                onRemove();
              }}
              okText="삭제"
              cancelText="취소">
              <Button size="small" danger type="text" onClick={(e) => e.stopPropagation()}>
                삭제
              </Button>
            </Popconfirm>
          </div>
        </div>

        <div
          style={{
            fontWeight: 600,
            fontSize: 13,
            color: '#333',
            marginBottom: 4,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={sc.title || '(제목 없음)'}>
          제목: {sc.title || <span style={{ color: '#bbb' }}>(제목 없음)</span>}
        </div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          상품 {productCount}개
        </Text>
        <div
          style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}
          onPointerDown={(e) => e.stopPropagation()}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>
              활성
            </Text>
            <Switch size="small" checked={sc.active} onChange={(checked) => onUpdate({ active: checked })} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>
              자동 스와이프
            </Text>
            <Switch size="small" checked={sc.autoplay} onChange={(checked) => onUpdate({ autoplay: checked })} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <Text type="secondary" style={{ fontSize: 11, color: sc.autoplay ? undefined : '#ccc' }}>
              간격(초)
            </Text>
            <InputNumber
              size="small"
              min={1}
              max={60}
              value={sc.delay}
              disabled={!sc.autoplay}
              onChange={(v) => onUpdate({ delay: v ?? 4 })}
              style={{ width: 64 }}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

// ─── 기획전 상품 추가용 체크박스 모달 ────────────────────────────────────────

interface CarouselProductPickerProps {
  open: boolean;
  productList: ProductModel[];
  productTypeMap: Record<string, string>;
  selectedIds: string[];
  onConfirm: (ids: string[]) => void;
  onClose: () => void;
}

const CarouselProductPicker: React.FC<CarouselProductPickerProps> = ({
  open,
  productList,
  productTypeMap,
  selectedIds,
  onConfirm,
  onClose,
}) => {
  const [checked, setChecked] = useState<string[]>(selectedIds);

  useEffect(() => {
    if (open) setChecked(selectedIds);
  }, [open]);

  const toggle = (id: string) =>
    setChecked((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));

  const cols: ColumnsType<ProductModel> = [
    {
      title: '',
      width: 44,
      render: (record: ProductModel) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={checked.includes(record._id)} onChange={() => toggle(record._id)} />
        </div>
      ),
    },
    {
      title: '썸네일',
      dataIndex: 'thumbnailImage',
      width: 52,
      render: (src) => (
        <img src={cdnImageUrl(src)} style={{ width: 34, height: 34, objectFit: 'cover', borderRadius: 4 }} />
      ),
    },
    { title: '상품명', dataIndex: 'title' },
    { title: '판매자', dataIndex: 'sellerName', width: 110 },
    {
      title: '금액',
      width: 100,
      render: (record: ProductModel) => {
        const d = record.applyPeriodDiscount && record.discountInfo?.discountedFee;
        return (
          <div style={{ fontSize: 12 }}>
            {d && <div style={{ textDecoration: 'line-through', color: '#999' }}>{record.fee?.toLocaleString()}원</div>}
            <div style={{ color: d ? '#e53e3e' : 'inherit', fontWeight: d ? 600 : 400 }}>
              {(d ? record.discountInfo?.discountedFee : record.fee)?.toLocaleString()}원
            </div>
          </div>
        );
      },
    },
    {
      title: '유형',
      width: 90,
      render: (record: ProductModel) => <TimeSalesTypeTag type={productTypeMap[record._id]} />,
    },
  ];

  return (
    <Modal
      open={open}
      title={`상품 추가 (${checked.length}개 선택)`}
      width={900}
      onCancel={onClose}
      onOk={() => onConfirm(checked)}
      okText="적용"
      cancelText="취소"
      styles={{ body: { maxHeight: 480, overflow: 'auto' } }}>
      <Table
        size="small"
        columns={cols}
        dataSource={productList}
        rowKey={(r) => r._id}
        pagination={false}
        onRow={(record) => ({ onClick: () => toggle(record._id), style: { cursor: 'pointer' } })}
      />
    </Modal>
  );
};

// ─── 기획전 편집 모달 ─────────────────────────────────────────────────────────

interface CarouselEditModalProps {
  open: boolean;
  carousel: SubCarousel | null;
  productList: ProductModel[];
  productTypeMap: Record<string, string>;
  onConfirm: (updated: SubCarousel) => void;
  onClose: () => void;
}

const CarouselEditModal: React.FC<CarouselEditModalProps> = ({
  open,
  carousel,
  productList,
  productTypeMap,
  onConfirm,
  onClose,
}) => {
  const [draft, setDraft] = useState<SubCarousel | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (open && carousel) setDraft({ ...carousel, selectedProductIds: [...carousel.selectedProductIds] });
  }, [open, carousel]);

  useEffect(() => {
    if (!open) setPickerOpen(false);
  }, [open]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const onProductSortEnd = (event: DragEndEvent) => {
    if (!draft) return;
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIdx = draft.selectedProductIds.indexOf(active.id as string);
      const newIdx = draft.selectedProductIds.indexOf(over?.id as string);
      setDraft((d) => d && { ...d, selectedProductIds: arrayMove(d.selectedProductIds, oldIdx, newIdx) });
    }
  };

  const handlePickerConfirm = (ids: string[]) => {
    setDraft((d) => d && { ...d, selectedProductIds: ids });
    setPickerOpen(false);
  };

  const removeProduct = (id: string) => {
    setDraft((d) => d && { ...d, selectedProductIds: d.selectedProductIds.filter((i) => i !== id) });
  };

  if (!draft) return null;

  const selectedProducts = draft.selectedProductIds
    .map((id) => productList.find((p) => p._id === id))
    .filter(Boolean) as ProductModel[];

  return (
    <Modal
      open={open}
      title="기획전 편집"
      width={700}
      onCancel={onClose}
      onOk={() => onConfirm(draft)}
      okText="적용"
      cancelText="취소"
      destroyOnClose>
      {/* 타이틀 */}
      <div style={{ marginBottom: 20 }}>
        <Input
          placeholder="기획전 타이틀 입력"
          value={draft.title}
          onChange={(e) => setDraft((d) => d && { ...d, title: e.target.value })}
        />
      </div>

      {/* 상품 목록 헤더 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <Text strong>
          상품 목록{' '}
          <Text type="secondary" style={{ fontWeight: 400 }}>
            ({selectedProducts.length}개)
          </Text>
        </Text>
        <Button size="small" icon={<PlusOutlined />} onClick={() => setPickerOpen(true)}>
          상품 추가
        </Button>
      </div>

      {/* 드래그 가능한 상품 목록 */}
      {selectedProducts.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            color: '#bbb',
            padding: '24px 0',
            border: '1px dashed #e0e0e0',
            borderRadius: 6,
          }}>
          상품 추가 버튼을 눌러 상품을 선택해주세요.
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onProductSortEnd}>
          <SortableContext items={draft.selectedProductIds} strategy={verticalListSortingStrategy}>
            <div
              style={{
                border: '1px solid #f0f0f0',
                borderRadius: 6,
                overflow: 'hidden',
                maxHeight: 380,
                overflowY: 'auto',
              }}>
              {selectedProducts.map((p, idx) => (
                <SortableProductRow
                  key={p._id}
                  product={p}
                  idx={idx}
                  productType={productTypeMap[p._id]}
                  onRemove={() => removeProduct(p._id)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <CarouselProductPicker
        open={pickerOpen}
        productList={productList}
        productTypeMap={productTypeMap}
        selectedIds={draft.selectedProductIds}
        onConfirm={handlePickerConfirm}
        onClose={() => setPickerOpen(false)}
      />
    </Modal>
  );
};

// ─── 모달 내 드래그 가능한 상품 행 ───────────────────────────────────────────

interface SortableProductRowProps {
  product: ProductModel;
  idx: number;
  productType?: string;
  onRemove: () => void;
}

const SortableProductRow: React.FC<SortableProductRowProps> = ({ product, idx, productType, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: product._id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    background: isDragging ? '#f0f9ff' : idx % 2 === 0 ? '#fff' : '#fafafa',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 12px',
    borderBottom: '1px solid #f0f0f0',
  };

  return (
    <div ref={setNodeRef} style={style}>
      <span
        {...attributes}
        {...listeners}
        style={{ cursor: isDragging ? 'grabbing' : 'grab', color: '#bbb', fontSize: 16 }}>
        <HolderOutlined />
      </span>
      <Text type="secondary" style={{ minWidth: 20, fontSize: 12 }}>
        {idx + 1}
      </Text>
      <img
        src={cdnImageUrl(product.thumbnailImage)}
        style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
      />
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
          {product.title}
        </div>
        <div style={{ fontSize: 11, color: '#999' }}>{product.sellerName}</div>
      </div>
      <TimeSalesTypeTag type={productType} />
      <Button
        size="small"
        danger
        type="text"
        icon={<DeleteOutlined />}
        onClick={onRemove}
        onPointerDown={(e) => e.stopPropagation()}
      />
    </div>
  );
};

// ─── 메인 페이지 ─────────────────────────────────────────────────────────────

const TimeSalesPage: React.FC = () => {
  useAuthentication();

  const queryClient = useQueryClient();

  const [selectModalOpen, setSelectModalOpen] = useState(false);
  const [productList, setProductList] = useState<ProductModel[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [productTypeMap, setProductTypeMap] = useState<Record<string, string>>({});
  const [editProductId, setEditProductId] = useState<string | undefined>(undefined);

  const [subCarousels, setSubCarousels] = useState<SubCarousel[]>([]);
  const [editingCarouselId, setEditingCarouselId] = useState<string | null>(null);

  const feedQuery = useQuery({
    queryKey: ['time-sales-feed'],
    queryFn: () => feedsV2Api.getTimeSalesFeed(),
  });

  const updateMutation = useMutation({
    mutationFn: feedsV2Api.updateTimeSalesFeed,
    onSuccess: () => {
      message.success('저장되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['time-sales-feed'] });
    },
    onError: () => message.error('저장 중 오류가 발생했습니다.'),
  });

  // ─ 피드 로드 ──────────────────────────────────────────────────────────────

  useEffect(() => {
    const feedData = feedQuery.data?.data;
    if (!feedData) return;

    const sections = feedData.data || [];

    setSubCarousels(
      sections
        .filter((s) => s.type === 'sub-carousel')
        .map((s) => ({
          id: Math.random().toString(36).slice(2),
          title: s.title || '',
          active: s.active ?? true,
          autoplay: s.autoplay ?? false,
          delay: s.delay ?? 4,
          selectedProductIds: (s.data || []).map((item) => item._id).filter(Boolean) as string[],
        })),
    );

    const productListData = sections.find((s) => s.type === 'product-list')?.data || [];
    const savedIds = productListData.map((item: any) => item._id).filter(Boolean) as string[];

    const savedTypeMap: Record<string, string> = {};
    productListData.forEach((item: any) => {
      if (item._id) savedTypeMap[item._id] = item.type || '얼리버드';
    });
    setProductTypeMap(savedTypeMap);

    if (savedIds.length === 0) return setProductList([]);

    setIsLoadingProducts(true);
    Promise.all(savedIds.map((id) => productApi.getProduct(id).then((r) => r.data)))
      .then((products) => setProductList(products.filter(Boolean)))
      .catch(() => message.error('상품 정보를 불러오는 중 오류가 발생했습니다.'))
      .finally(() => setIsLoadingProducts(false));
  }, [feedQuery.data]);

  // ─ 전체 상품 목록 ────────────────────────────────────────────────────────

  const refreshProductList = () => {
    const ids = productList.map((p) => p._id);
    if (ids.length === 0) return;
    setIsLoadingProducts(true);
    Promise.all(ids.map((id) => productApi.getProduct(id).then((r) => r.data)))
      .then((products) => setProductList(products.filter(Boolean)))
      .catch(() => message.error('상품 정보를 불러오는 중 오류가 발생했습니다.'))
      .finally(() => setIsLoadingProducts(false));
  };

  const handleAddProducts = (products: ProductModel[]) => {
    setProductList((prev) => {
      const ids = new Set(prev.map((p) => p._id));
      return [...prev, ...products.filter((p) => !ids.has(p._id))];
    });
    setProductTypeMap((prev) => {
      const next = { ...prev };
      products.forEach((p) => {
        if (!next[p._id]) next[p._id] = '얼리버드';
      });
      return next;
    });
    setSelectModalOpen(false);
  };

  const handleRemoveProduct = (productId: string) => {
    setProductList((prev) => prev.filter((p) => p._id !== productId));
    setProductTypeMap((prev) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [productId]: _removed, ...rest } = prev;
      return rest;
    });
    setSubCarousels((prev) =>
      prev.map((sc) => ({ ...sc, selectedProductIds: sc.selectedProductIds.filter((id) => id !== productId) })),
    );
  };

  // ─ DnD ────────────────────────────────────────────────────────────────────

  const sortableSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const onProductSortEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oi = productList.findIndex((p) => p._id === active.id);
      const ni = productList.findIndex((p) => p._id === over?.id);
      setProductList((prev) => arrayMove(prev, oi, ni));
    }
  };

  const onCarouselSortEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oi = subCarousels.findIndex((sc) => sc.id === active.id);
      const ni = subCarousels.findIndex((sc) => sc.id === over?.id);
      setSubCarousels((prev) => arrayMove(prev, oi, ni));
    }
  };

  // ─ 기획전 핸들러 ─────────────────────────────────────────────────────────

  const addSubCarousel = () => {
    const newId = Math.random().toString(36).slice(2);
    setSubCarousels((prev) => [
      ...prev,
      { id: newId, title: '', active: true, autoplay: false, delay: 4, selectedProductIds: [] },
    ]);
    setEditingCarouselId(newId);
  };

  const updateSubCarousel = (id: string, patch: Partial<SubCarousel>) => {
    setSubCarousels((prev) => prev.map((sc) => (sc.id === id ? { ...sc, ...patch } : sc)));
  };

  const removeSubCarousel = (id: string) => setSubCarousels((prev) => prev.filter((sc) => sc.id !== id));

  const handleEditConfirm = (updated: SubCarousel) => {
    setSubCarousels((prev) =>
      prev.map((sc) =>
        sc.id === updated.id ? { ...sc, title: updated.title, selectedProductIds: updated.selectedProductIds } : sc,
      ),
    );
    setEditingCarouselId(null);
  };

  // ─ 저장 ──────────────────────────────────────────────────────────────────

  const handleSave = () => {
    updateMutation.mutate({
      products: productList.map((p) => ({ _id: p._id, type: productTypeMap[p._id] || '얼리버드' })),
      subCarousels: subCarousels.map((sc) => ({
        title: sc.title,
        active: sc.active,
        autoplay: sc.autoplay,
        delay: sc.delay,
        productIds: sc.selectedProductIds,
      })),
    });
  };

  // ─ 전체 상품 목록 테이블 컬럼 ────────────────────────────────────────────

  const productColumns: ColumnsType<ProductModel> = [
    {
      title: '',
      width: 32,
      render: () => <HolderOutlined style={{ color: '#c0c0c0', fontSize: 15, cursor: 'pointer' }} />,
    },
    { title: '#', width: 50, render: (_, __, i) => i + 1 },
    {
      title: '썸네일',
      dataIndex: 'thumbnailImage',
      width: 60,
      render: (src) => (
        <img src={cdnImageUrl(src)} style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
      ),
    },
    { title: '판매방식', dataIndex: 'saleType', width: 90 },
    {
      title: '타입선택',
      width: 130,
      render: (_: any, record: ProductModel) => (
        <div onPointerDown={(e) => e.stopPropagation()}>
          <Select
            size="small"
            value={productTypeMap[record._id] || '얼리버드'}
            onChange={(value) => setProductTypeMap((prev) => ({ ...prev, [record._id]: value }))}
            options={[
              { label: '얼리버드', value: '얼리버드' },
              { label: '오픈특가', value: '오픈특가' },
            ]}
            style={{ width: 110 }}
          />
        </div>
      ),
    },
    {
      title: '상품명',
      dataIndex: 'title',
      width: 280,
      render: (title: string) => (
        <div style={{ maxWidth: 280, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.5 }}>{title}</div>
      ),
    },
    { title: '판매자', dataIndex: 'sellerName', width: 120 },
    {
      title: '금액',
      width: 130,
      render: (record: ProductModel) => {
        const d = record.applyPeriodDiscount && record.discountInfo?.discountedFee;
        return (
          <div>
            {d && (
              <div style={{ textDecoration: 'line-through', color: '#999', fontSize: 12 }}>
                {record.fee?.toLocaleString()}원
              </div>
            )}
            <div style={{ fontWeight: d ? 600 : 400, color: d ? '#e53e3e' : 'inherit' }}>
              {(d ? record.discountInfo?.discountedFee : record.fee)?.toLocaleString()}원
            </div>
          </div>
        );
      },
    },
    {
      title: '할인 기간',
      width: 200,
      render: (record: ProductModel) => {
        const { discountStartDate, discountEndDate } = record.discountInfo || {};
        if (!discountStartDate && !discountEndDate) return <span style={{ color: '#bbb' }}>-</span>;
        return (
          <div style={{ fontSize: 12, lineHeight: '1.6' }}>
            {discountStartDate && <div>시작: {moment(discountStartDate).format('YYYY.MM.DD HH:mm')}</div>}
            {discountEndDate && (
              <div style={{ color: new Date(discountEndDate) < new Date() ? '#e53e3e' : '#38a169' }}>
                종료: {moment(discountEndDate).format('YYYY.MM.DD HH:mm')}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: '할인상태',
      width: 90,
      render: (record: ProductModel) => {
        if (!record.applyPeriodDiscount) return <Tag color="default">-</Tag>;
        const endDate = record.discountInfo?.discountEndDate;
        if (endDate && new Date(endDate) < new Date()) return <Tag color="default">할인종료</Tag>;
        return <Tag color="green">할인중</Tag>;
      },
    },
    {
      title: '상태',
      dataIndex: 'status',
      width: 80,
      render: (s) => {
        const c: Record<string, string> = { open: 'green', close: 'default', private: 'orange' };
        return <Tag color={c[s] || 'default'}>{s}</Tag>;
      },
    },
    {
      title: '수정일',
      dataIndex: 'updatedAt',
      width: 130,
      render: (v) => moment(v).format('YYYY.MM.DD HH:mm'),
    },
    {
      title: '편집',
      width: 70,
      fixed: 'right',
      render: (record: ProductModel) => (
        <div onPointerDown={(e) => e.stopPropagation()}>
          <Button size="small" onClick={() => setEditProductId(record._id)}>
            편집
          </Button>
        </div>
      ),
    },
    {
      title: '삭제',
      width: 70,
      fixed: 'right',
      render: (record: ProductModel) => (
        <Popconfirm
          title="목록에서 제거하시겠습니까?"
          onConfirm={() => handleRemoveProduct(record._id)}
          okText="제거"
          cancelText="취소">
          <Button danger size="small">
            삭제
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const editingCarousel = subCarousels.find((sc) => sc.id === editingCarouselId) || null;

  // ─ 렌더링 ────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '24px' }}>
      {/* 페이지 헤더 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>
          타임세일 관리
        </Title>
        <Button type="primary" loading={updateMutation.isPending} onClick={handleSave}>
          저장
        </Button>
      </div>

      {/* ── 기획전 섹션 ─────────────────────────────────────────── */}
      <Card
        title="기획전"
        extra={
          <Button type="dashed" icon={<PlusOutlined />} onClick={addSubCarousel}>
            추가
          </Button>
        }
        style={{ marginBottom: 24 }}>
        {subCarousels.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#bbb', padding: '24px 0' }}>
            추가 버튼을 눌러 기획전을 만들어 보세요.
          </div>
        ) : (
          <DndContext sensors={sortableSensors} collisionDetection={closestCenter} onDragEnd={onCarouselSortEnd}>
            <SortableContext items={subCarousels.map((sc) => sc.id)} strategy={horizontalListSortingStrategy}>
              <div
                style={{
                  display: 'flex',
                  gap: 12,
                  overflowX: 'auto',
                  paddingBottom: 8,
                  alignItems: 'flex-start',
                }}>
                {subCarousels.map((sc) => (
                  <SortableCarouselCard
                    key={sc.id}
                    sc={sc}
                    productCount={sc.selectedProductIds.length}
                    onEdit={() => setEditingCarouselId(sc.id)}
                    onRemove={() => removeSubCarousel(sc.id)}
                    onUpdate={(patch) => updateSubCarousel(sc.id, patch)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </Card>

      {/* ── 전체 상품 목록 섹션 ────────────────────────────────────────── */}
      <Divider orientation="left" style={{ margin: '0 0 16px' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Title level={5} style={{ margin: 0 }}>
            전체 상품 목록 (product-list)
          </Title>
          <Text type="secondary" style={{ fontSize: 13 }}>
            총 {productList.length}개 · 행 드래그로 순서 변경
          </Text>
        </div>
        <Button onClick={() => setSelectModalOpen(true)}>상품 불러오기</Button>
      </div>

      <DndContext sensors={sortableSensors} collisionDetection={closestCenter} onDragEnd={onProductSortEnd}>
        <SortableContext items={productList.map((p) => p._id)} strategy={verticalListSortingStrategy}>
          <Table
            columns={productColumns}
            dataSource={productList}
            rowKey={(r) => r._id}
            loading={feedQuery.isLoading || isLoadingProducts}
            pagination={false}
            tableLayout="fixed"
            scroll={{ x: 1622 }}
            components={{
              body: {
                row: ({ 'data-row-key': id, ...rest }: any) => <SortableRow id={id} {...rest} />,
              },
            }}
          />
        </SortableContext>
      </DndContext>

      {/* 전체 상품 선택 모달 */}
      <SelectProductModal
        open={selectModalOpen}
        onAddProducts={handleAddProducts}
        onClose={() => setSelectModalOpen(false)}
        disabledProductIds={productList.map((product) => product._id)}
      />

      {/* 기획전 편집 모달 */}
      <CarouselEditModal
        open={!!editingCarouselId}
        carousel={editingCarousel}
        productList={productList}
        productTypeMap={productTypeMap}
        onConfirm={handleEditConfirm}
        onClose={() => setEditingCarouselId(null)}
      />
      {editProductId && (
        <ProductEditView
          productId={editProductId}
          onClose={() => setEditProductId(undefined)}
          needRefetch={refreshProductList}
        />
      )}
    </div>
  );
};

export default TimeSalesPage;
